import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { bankersRound } from '@components-clean/core/charts/shared/number-utils';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import {
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import { useTranslation } from '@hooks/index';
import { convertColorEnumToString } from '@utils/convertor';

type RecordProps = {
    AmberCount: number;
    GreenCount: number;
    PurpleCount: number;
    RedCount: number;
    SiteScore?: number;
    Comments?: string;
    DataSetDate?: Date;
    DatasetId: number;
    Decision?: string;
    DatasetOpenedOn: number;
    IsDeleted: boolean;
    Observation: string;
    RAG?: string;
    Score?: number;
    XAxisTitle?: string;
    XAxisValue?: number;
    YAxisTitle?: string;
    YAxisValue?: number;
    YVolume?: number;
    Type: number;
    IsScatterPlot: boolean;
    IsSystemGenerated: boolean;
    YValue?: number;
    DenominatorCount?: number;
    XVolume?: number;
    StudySiteMean?: number;
    StudyRate?: number;
    OutOfToleranceValue?: number;
};

type ReviewLogChangeGridProps = {
    oldRecord: Partial<RecordProps>;
    newRecord: Partial<RecordProps>;
    oldItem?: Record<string, unknown>;
    newItem?: Record<string, unknown>;
};

const CM2ReviewLogRecordChangeGrid = ({ oldRecord, newRecord, oldItem, newItem }: ReviewLogChangeGridProps) => {
    const { t } = useTranslation();

    if (isEmpty(oldRecord) && isEmpty(newRecord)) return null;

    const isStudy = (newRecord.Type === 1 || oldRecord.Type === 1) ?? false;
    const IsScatterPlot = (newRecord.IsScatterPlot || oldRecord.IsScatterPlot) ?? false;
    const isControls = (newRecord.Type === 2 || oldRecord.Type === 2) ?? false;

    const propArray: string[] = [
        'IsDeleted',
        'DatasetId',
        'Observation',
        'IsScatterPlot',
        'DataSetDate',
        'IsSystemGenerated',
        'Score',
        ...(isStudy && !IsScatterPlot
            ? [
                  'GreenCount',
                  'AmberCount',
                  'RedCount',
                  'PurpleCount',
                  'XVolume',
                  'YVolume',
                  'StudyRate',
                  'StudySiteMean',
              ]
            : []),
        ...(!isStudy && !IsScatterPlot
            ? [
                  'XAxisTitle',
                  'YAxisTitle',
                  'YVolume',
                  'PanelScore',
                  'RAG',
                  'XAxisValue',
                  'YAxisValue',
                  'XVolume',
                  'YVolume',
                  'StudyRate',
                  'StudySiteMean',
              ]
            : []),
        ...(isControls ? ['OOTDescription', 'ColorDirectionThreshold', 'ScaleBy', 'OutOfToleranceValue'] : []),
        'DecisionId',
    ];

    const refactoredOldRecord = {
        ...oldRecord,
        ...removePropertiesFromObject(oldRecord, propArray),
        RAG:
            !isStudy && !IsScatterPlot
                ? undefined
                : !isEmpty(oldRecord)
                  ? oldRecord.RAG
                      ? convertColorEnumToString(+oldRecord.RAG)
                      : ''
                  : '',
        PanelScore:
            !isStudy && !IsScatterPlot
                ? undefined
                : oldRecord.Score !== undefined
                  ? bankersRound(oldRecord.Score, 2)
                  : '',
        XAxisValue:
            !isStudy && !IsScatterPlot
                ? undefined
                : oldRecord.XAxisValue !== undefined
                  ? bankersRound(oldRecord.XAxisValue, 2)
                  : '',
        YAxisValue:
            !isStudy && !IsScatterPlot
                ? undefined
                : oldRecord.YAxisValue !== undefined
                  ? bankersRound(oldRecord.YAxisValue, 2)
                  : '',
        YVolume:
            !isControls && IsScatterPlot
                ? oldRecord.YVolume !== undefined
                    ? bankersRound(oldRecord.YVolume, 2)
                    : ''
                : undefined,
        StudySiteMean:
            isStudy && IsScatterPlot
                ? oldRecord.StudySiteMean !== undefined
                    ? bankersRound(oldRecord.StudySiteMean, 2)
                    : ''
                : undefined,
        StudyRate:
            isStudy && IsScatterPlot
                ? oldRecord.StudyRate !== undefined
                    ? bankersRound(oldRecord.StudyRate, 2)
                    : ''
                : undefined,
        XVolume:
            isStudy && IsScatterPlot
                ? oldRecord.XVolume !== undefined
                    ? bankersRound(oldRecord.XVolume, 2)
                    : ''
                : undefined,
        Type: !isEmpty(oldRecord) ? (oldRecord.IsSystemGenerated ? t('auditLog:system') : t('auditLog:manual')) : '',
        Dataset: oldRecord.DataSetDate,
        YValue: oldRecord.YValue !== undefined ? bankersRound(oldRecord.YValue, 2) : undefined,
        DenominatorCount:
            oldRecord.DenominatorCount !== undefined ? bankersRound(oldRecord.DenominatorCount, 2) : undefined,
        ToleranceLimit:
            oldRecord.OutOfToleranceValue !== undefined ? bankersRound(oldRecord.OutOfToleranceValue, 2) : undefined,
    } as Record<string, unknown>;

    const refactoredNewRecord = {
        ...newRecord,
        ...removePropertiesFromObject(newRecord, propArray),
        RAG:
            !isStudy && !IsScatterPlot
                ? undefined
                : !isEmpty(newRecord)
                  ? newRecord.RAG
                      ? convertColorEnumToString(+newRecord.RAG)
                      : ''
                  : '',
        PanelScore:
            !isStudy && !IsScatterPlot ? undefined : newRecord.Score != null ? bankersRound(newRecord.Score, 2) : '',
        XAxisValue:
            !isStudy && !IsScatterPlot
                ? undefined
                : newRecord.XAxisValue != null
                  ? bankersRound(newRecord.XAxisValue, 2)
                  : '',
        YAxisValue:
            !isStudy && !IsScatterPlot
                ? undefined
                : newRecord.YAxisValue != null
                  ? bankersRound(newRecord.YAxisValue, 2)
                  : '',
        YVolume:
            !isControls && IsScatterPlot
                ? newRecord.YVolume != null
                    ? bankersRound(newRecord.YVolume, 2)
                    : ''
                : undefined,
        StudySiteMean:
            isStudy && IsScatterPlot
                ? newRecord.StudySiteMean != null
                    ? bankersRound(newRecord.StudySiteMean, 2)
                    : ''
                : undefined,
        StudyRate:
            isStudy && IsScatterPlot
                ? newRecord.StudyRate != null
                    ? bankersRound(newRecord.StudyRate, 2)
                    : ''
                : undefined,
        XVolume:
            isStudy && IsScatterPlot
                ? newRecord.XVolume != null
                    ? bankersRound(newRecord.XVolume, 2)
                    : ''
                : undefined,
        Type: !isEmpty(newRecord) ? (newRecord.IsSystemGenerated ? t('auditLog:system') : t('auditLog:manual')) : '',
        Dataset: newRecord.DataSetDate,
        YValue: newRecord.YValue != null ? bankersRound(newRecord.YValue, 2) : undefined,
        DenominatorCount: newRecord.DenominatorCount != null ? bankersRound(newRecord.DenominatorCount, 2) : undefined,
        ToleranceLimit:
            newRecord.OutOfToleranceValue != null ? bankersRound(newRecord.OutOfToleranceValue, 2) : undefined,
    } as Record<string, unknown>;

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
        }),
    );

    return <FieldChangeGrid items={items} />;
};
export { CM2ReviewLogRecordChangeGrid };
